import React, {FunctionComponent, useContext, useState} from "react";
import "./VariantProposalModal.scss";
import {InvoiceOption} from "../../models/item.model";
import {Invoice} from "../../models/invoice.model";
import {InvoiceContext} from "../../context/InvoiceContext";
import {OptionContext} from "../../context/OptionContext";
import {AcornContext} from "../../context/AcornContext";
import {PreviewDataContext} from "../../context/PreviewContext";
import {FullScreenModal} from "../Modal/FullScreenModal";
import {Tabs} from "../ui-components/Tabs/Tabs";
import {InvoiceSummaryRows} from "../InvoiceSummary/InvoiceSummaryRows";
import {InvoiceActionRow} from "../InvoiceActionRow/InvoiceActionRow";
import {ContractContent} from "../ContractContent/ContractContent";
import {SignatureReject} from "../SignatureReject/SignatureReject";
import {SectionPreloader} from "../SectionPreloader/SectionPreloader";
import {PaymentModal} from "../PaymentModal/PaymentModal";
import AcornWidget from "../AcornWidget/AcornWidget";
import {usePayNow} from "../../hooks/usePayNow";
import {getVariantProposalOptionTitle} from "./VariantProposalCard";
import closeIcon from "../../assets/icons/times-solid.svg";

type AcceptanceTab = 'summary' | 'contract';

interface VariantProposalAcceptanceModalProps {
    option: InvoiceOption;
    onClose: () => void;
}

export const VariantProposalAcceptanceModal: FunctionComponent<VariantProposalAcceptanceModalProps> = ({option, onClose}) => {
    const invoice: Invoice = useContext(InvoiceContext);
    const optionContext = useContext(OptionContext);
    const isAcornEnabled: boolean = useContext(AcornContext);
    const previewContext = useContext(PreviewDataContext);
    const {onClickPayNow, isPaymentModalVisible, closePaymentModal} = usePayNow();
    const [activeTab, setActiveTab] = useState<AcceptanceTab>('summary');

    const hasContractTab = Boolean(invoice.is_contract_content_visible && invoice.contract_content && invoice.contract_content.length > 0);
    const isContractTab = hasContractTab && activeTab === 'contract';
    const loanAmount: number = optionContext.getAcornLoanAmount();

    return (
        <FullScreenModal customClass="variant-proposal-acceptance-modal">
            <header>
                <div className="acceptance-modal-title">
                    <span className="acceptance-modal-subtitle">You are accepting</span>
                    <h1>{getVariantProposalOptionTitle(option)}</h1>
                </div>
                <img onClick={onClose} className="image-icon desktop-2x clickable" src={closeIcon} alt="close"/>
            </header>

            {hasContractTab && <Tabs<AcceptanceTab> tabs={[{id: 'summary', label: 'Summary'}, {id: 'contract', label: 'Contract'}]}
                                                    activeTab={activeTab}
                                                    onChange={setActiveTab}/>}

            <main className="acceptance-modal-main">
                {!isContractTab &&
                    <section className="acceptance-summary-card">
                        <div className={`invoice-summary-loader ${optionContext.isCardFeeRecoverySummaryFetching ? '' : 'hidden'}`}>
                            <SectionPreloader/>
                        </div>
                        <h3>{invoice.invoice_name} Summary</h3>
                        <InvoiceSummaryRows/>
                    </section>}

                {isContractTab &&
                    <section className="acceptance-contract-card">
                        <h3>Contract</h3>
                        <ContractContent/>
                    </section>}
            </main>

            <footer className="acceptance-modal-footer">
                <div className="acceptance-action-row">
                    {isContractTab && invoice.is_signature_available && <SignatureReject/>}
                    <InvoiceActionRow onClickPayNow={onClickPayNow}/>
                </div>

                {isAcornEnabled && loanAmount > 0 &&
                    <AcornWidget isPreviewMode={previewContext.isPreviewMode} loanAmount={loanAmount}/>}
            </footer>

            {isPaymentModalVisible && <PaymentModal onCloseModal={closePaymentModal}/>}
        </FullScreenModal>
    );
};
